import type { InstrumentId } from "@trader/shared/MarketData"
import {
  type AmendOrder,
  type Fill,
  formatDecimal,
  type Order,
  type OrderId,
  type Position,
  type Product,
  type SetProtection
} from "@trader/shared/Money"
import { BigDecimal, Option } from "effect"
import { type AccountState, openOrdersOn, openPositionsOn } from "./accountStore.ts"
import { boxLabels } from "../chart/boxLabels.ts"
import { roundToStep } from "./orderTicket.ts"

// Orders and positions on the chart, the way TradingView's trading panel draws them
// (docs/design/0013), without the chart: which lines there are, which can be dragged or
// closed, the profit or loss at a price, and the request a confirmed drag sends.
// TradingLayer draws this and handles the pointer; this module is unit-tested.

type Decimal = BigDecimal.BigDecimal

export type LineKind = "order" | "position" | "stopLoss" | "takeProfit"

export interface ChartLine {
  /** Stable across redraws: the order id, or the position's product. */
  readonly key: string
  readonly kind: LineKind
  readonly price: Decimal
  /** Text on the line's label, e.g. "BUY LMT 10", "LONG 10", "SL". */
  readonly label: string
  /** Buy-ish (blue/green) or sell-ish (red) colouring. */
  readonly tone: "buy" | "sell" | "long" | "short" | "loss" | "profit"
  readonly draggable: boolean
  /** Shows a × (cancel the order, remove the leg, or close the position). */
  readonly closable: boolean
  readonly orderId: OrderId | null
  readonly product: Product
  /** The order's price field a drag moves (limit or stop); null for a position. */
  readonly field: "limitPrice" | "stopPrice" | null
  /** For a position or a working entry: whether it still lacks a stop-loss / take-profit (shows the SL / TP buttons). */
  readonly canAdd: { readonly stopLoss: boolean; readonly takeProfit: boolean } | null
  /** A working entry's own stop-loss / take-profit (not yet on a position): that entry's order id. */
  readonly parentOrderId: OrderId | null
}

const TYPE_LABEL = { Limit: "LMT", Stop: "STP", StopLimit: "STP LMT" } as const

const priceOf = (order: Order): { readonly price: Decimal; readonly field: "limitPrice" | "stopPrice" } | null => {
  switch (order.type._tag) {
    case "Market":
      return null
    case "Limit":
      return { price: order.type.limitPrice, field: "limitPrice" }
    case "Stop":
      return { price: order.type.stopPrice, field: "stopPrice" }
    case "StopLimit":
      // Triggered: only the limit can move; before, the stop is what the user watches.
      return order.status === "Triggered"
        ? { price: order.type.limitPrice, field: "limitPrice" }
        : { price: order.type.stopPrice, field: "stopPrice" }
  }
}

/** Every line the followed account has on this instrument. */
export const chartLines = (state: AccountState | null, instrumentId: InstrumentId | null): ReadonlyArray<ChartLine> => {
  if (state === null || instrumentId === null) return []
  const lines: Array<ChartLine> = []
  const archived = state.summary?.status === "Archived" || state.summary?.endedAt != null
  for (const order of openOrdersOn(state, instrumentId)) {
    const at = priceOf(order)
    if (at === null) continue
    const pending = order.status === "PendingCancel" || order.status === "PendingSubmit"
    // A strategy's orders are its own; square-offs are the account's.
    const manual = order.runnerId === null && !archived
    const qty = formatDecimal(BigDecimal.subtract(order.qty, order.filledQty))
    if (order.role === "StopLoss" || order.role === "TakeProfit") {
      lines.push({
        key: order.id,
        kind: order.role === "StopLoss" ? "stopLoss" : "takeProfit",
        price: at.price,
        label: order.role === "StopLoss" ? "SL" : "TP",
        tone: order.role === "StopLoss" ? "loss" : "profit",
        draggable: manual && !pending,
        closable: manual && !pending,
        orderId: order.id,
        product: order.product,
        field: at.field,
        canAdd: null,
        parentOrderId: null
      })
      continue
    }
    const movable = manual && !pending && order.role === "Entry" && !order.clientOrderId.startsWith("close-")
    // The stop-loss and take-profit it will put on the position when it fills (docs/design/0013).
    for (const leg of ["stopLoss", "takeProfit"] as const) {
      const price = order.protection?.[leg]
      if (price === undefined) continue
      lines.push({
        key: `${order.id}:${leg}`,
        kind: leg,
        price,
        label: leg === "stopLoss" ? "SL" : "TP",
        tone: leg === "stopLoss" ? "loss" : "profit",
        draggable: movable,
        closable: movable,
        orderId: null,
        product: order.product,
        field: null,
        canAdd: null,
        parentOrderId: order.id
      })
    }
    lines.push({
      key: order.id,
      kind: "order",
      price: at.price,
      label: `${order.side === "Buy" ? "BUY" : "SELL"} ${TYPE_LABEL[order.type._tag as keyof typeof TYPE_LABEL]} ${qty}`,
      tone: order.side === "Buy" ? "buy" : "sell",
      draggable: manual && !pending,
      closable: manual && !pending,
      orderId: order.id,
      product: order.product,
      field: at.field,
      canAdd: movable ? { stopLoss: order.protection?.stopLoss === undefined, takeProfit: order.protection?.takeProfit === undefined } : null,
      parentOrderId: null
    })
  }
  for (const position of openPositionsOn(state, instrumentId)) {
    if (position.avgPrice === null) continue
    const long = BigDecimal.isPositive(position.qty)
    const manual = position.runnerId === null && !archived
    lines.push({
      key: `position:${position.product}`,
      kind: "position",
      price: position.avgPrice,
      label: `${long ? "LONG" : "SHORT"} ${formatDecimal(BigDecimal.abs(position.qty))}`,
      tone: long ? "long" : "short",
      draggable: false,
      closable: manual,
      orderId: null,
      product: position.product,
      field: null,
      canAdd: manual ? { stopLoss: position.stopLoss === null, takeProfit: position.takeProfit === null } : null,
      parentOrderId: null
    })
  }
  return lines
}

/** A protected position as TradingView's long / short position tool (docs/design/0016). */
export interface PositionBox {
  readonly product: Product
  readonly long: boolean
  readonly entry: Decimal
  readonly stopLoss: Decimal | null
  readonly takeProfit: Decimal | null
}

/**
 * A box for each position with a stop-loss or take-profit, from its average price to them. The
 * lines include drafts, so the box follows a stop-loss being dragged or placed before Confirm.
 */
export const positionBoxes = (lines: ReadonlyArray<ChartLine>): Array<PositionBox> => {
  const boxes: Array<PositionBox> = []
  for (const line of lines) {
    if (line.kind !== "position") continue
    // The position's own legs (a working entry's legs have their order as parent).
    const leg = (kind: "stopLoss" | "takeProfit") =>
      lines.find((other) => other.kind === kind && other.parentOrderId === null && other.product === line.product)?.price ?? null
    const stopLoss = leg("stopLoss")
    const takeProfit = leg("takeProfit")
    if (stopLoss === null && takeProfit === null) continue
    boxes.push({ product: line.product, long: line.tone === "long", entry: line.price, stopLoss, takeProfit })
  }
  return boxes
}

/** The position tool's labels for an open position (chart/boxLabels.ts). */
export const positionBoxLabels = (position: Position, box: PositionBox, tickSize: Decimal, currency: string) =>
  boxLabels({
    entry: BigDecimal.toNumberUnsafe(box.entry),
    stopLoss: box.stopLoss === null ? null : BigDecimal.toNumberUnsafe(box.stopLoss),
    takeProfit: box.takeProfit === null ? null : BigDecimal.toNumberUnsafe(box.takeProfit),
    qty: position.qty,
    pnl: position.unrealizedPnl,
    pnlName: "Open P&L"
  }, tickSize, currency)

/**
 * When the position opened: the fill that took it from flat (or across zero), reading the fills
 * (newest first) back from its quantity; null when the loaded fills don't reach back that far.
 */
export const positionOpenedAt = (fills: ReadonlyArray<Fill>, position: Position): number | null => {
  let rest = position.qty
  for (const fill of fills) {
    if (fill.instrumentId !== position.instrumentId || fill.product !== position.product) continue
    rest = BigDecimal.subtract(rest, fill.side === "Buy" ? fill.qty : BigDecimal.negate(fill.qty))
    if (BigDecimal.isZero(rest) || BigDecimal.isPositive(rest) !== BigDecimal.isPositive(position.qty)) return fill.time
  }
  return null
}

/** A finished paper trade: from the fill that opened the position to the one that closed it. */
export interface ClosedTrade {
  readonly product: Product
  readonly long: boolean
  readonly openedAt: number
  readonly closedAt: number
  /** Average prices of the fills that opened (and added to) it and of those that closed it. */
  readonly entry: number
  readonly exit: number
  /** Its largest size, and the P&L its closing fills realized. */
  readonly qty: Decimal
  readonly pnl: Decimal
  /** The last stop-loss and take-profit it had (placed while it was open), null for none. */
  readonly stopLoss: number | null
  readonly takeProfit: number | null
}

/**
 * The account's finished trades on an instrument, from its fills (a trade runs from flat to flat;
 * a reversal closes one and opens the next) and the stop-loss / take-profit orders placed while
 * each was open. Only what the loaded fills and order history show.
 */
export const closedTrades = (
  fills: ReadonlyArray<Fill>,
  orders: ReadonlyArray<Order>,
  instrumentId: InstrumentId
): Array<ClosedTrade> => {
  const trades: Array<ClosedTrade> = []
  const n = BigDecimal.toNumberUnsafe
  interface Open {
    readonly long: boolean
    readonly openedAt: number
    qty: number
    entryQty: number
    entryValue: number
    exitQty: number
    exitValue: number
    pnl: Decimal
  }
  const open = new Map<Product, Open>()
  const legPrice = (product: Product, role: "StopLoss" | "TakeProfit", from: number, to: number): number | null => {
    let latest: Order | undefined
    for (const order of orders) {
      if (order.instrumentId !== instrumentId || order.product !== product || order.role !== role) continue
      if (order.createdAt < from || order.createdAt > to) continue
      if (latest === undefined || order.createdAt > latest.createdAt || (order.createdAt === latest.createdAt && order.seq > latest.seq)) latest = order
    }
    if (latest === undefined) return null
    const at = priceOf(latest)
    return at === null ? null : n(at.price)
  }
  // Oldest first.
  const ordered = fills.filter((fill) => fill.instrumentId === instrumentId).sort((a, b) => a.time - b.time || a.seq - b.seq)
  for (const fill of ordered) {
    const signed = fill.side === "Buy" ? n(fill.qty) : -n(fill.qty)
    const price = n(fill.price)
    let rest = signed
    const current = open.get(fill.product)
    if (current !== undefined) {
      const sign = current.long ? 1 : -1
      if (Math.sign(rest) === sign) {
        current.qty += rest
        current.entryQty += Math.abs(rest)
        current.entryValue += Math.abs(rest) * price
        continue
      }
      const closing = Math.min(Math.abs(rest), Math.abs(current.qty))
      current.exitQty += closing
      current.exitValue += closing * price
      current.pnl = BigDecimal.sum(current.pnl, fill.realizedPnl)
      current.qty += sign * -closing
      rest += sign * closing
      if (Math.abs(current.qty) > 1e-9) continue
      open.delete(fill.product)
      trades.push({
        product: fill.product,
        long: current.long,
        openedAt: current.openedAt,
        closedAt: fill.time,
        entry: current.entryValue / current.entryQty,
        exit: current.exitValue / current.exitQty,
        qty: BigDecimal.fromNumberUnsafe(current.entryQty),
        pnl: current.pnl,
        stopLoss: legPrice(fill.product, "StopLoss", current.openedAt, fill.time),
        takeProfit: legPrice(fill.product, "TakeProfit", current.openedAt, fill.time)
      })
    }
    // What's left opens a trade (a fresh one, or the other side of a reversal).
    if (Math.abs(rest) > 1e-9) {
      open.set(fill.product, { long: rest > 0, openedAt: fill.time, qty: rest, entryQty: Math.abs(rest), entryValue: Math.abs(rest) * price, exitQty: 0, exitValue: 0, pnl: BigDecimal.fromBigInt(0n) })
    }
  }
  return trades
}

/** The position a line belongs to (its own, or the one an SL / TP protects). */
export const positionFor = (state: AccountState | null, instrumentId: InstrumentId | null, product: Product): Position | undefined =>
  state === null || instrumentId === null ? undefined : openPositionsOn(state, instrumentId).find((position) => position.product === product)

/** The profit (positive) or loss of closing the whole position at `price`. */
export const pnlAt = (position: Position, price: Decimal): Decimal | null =>
  position.avgPrice === null ? null : BigDecimal.multiply(BigDecimal.subtract(price, position.avgPrice), position.qty)

/** A chart price (a float from the pointer) on the tick grid, never at or below zero. */
export const snapPrice = (price: number, tickSize: Decimal): Decimal | null => {
  if (!Number.isFinite(price) || price <= 0) return null
  const parsed = BigDecimal.fromNumber(price)
  if (Option.isNone(parsed)) return null
  const snapped = roundToStep(parsed.value, tickSize)
  return BigDecimal.isPositive(snapped) ? snapped : tickSize
}

const ONE_PERCENT = BigDecimal.fromStringUnsafe("0.01")

/**
 * Where a new stop-loss or take-profit starts (before it's dragged): 1% beyond the nearer of
 * the average and the last price on its side, so it starts where the server accepts it.
 */
export const startingPrice = (position: Position, leg: "stopLoss" | "takeProfit", tickSize: Decimal): Decimal | null => {
  if (position.avgPrice === null) return null
  const mark = position.mark ?? position.avgPrice
  const long = BigDecimal.isPositive(position.qty)
  const below = leg === "stopLoss" ? long : !long
  const base = below ? BigDecimal.min(position.avgPrice, mark) : BigDecimal.max(position.avgPrice, mark)
  const offset = BigDecimal.multiply(base, ONE_PERCENT)
  const price = below ? BigDecimal.subtract(base, offset) : BigDecimal.sum(base, offset)
  const snapped = roundToStep(price, tickSize)
  return BigDecimal.isPositive(snapped) ? snapped : null
}

/** What a confirmed drag (or a new SL / TP) sends. */
export type LineRequest =
  | { readonly _tag: "Amend"; readonly orderId: OrderId; readonly prices: AmendOrder }
  | { readonly _tag: "Protect"; readonly instrumentId: InstrumentId; readonly body: SetProtection }

/** A working order or an existing SL / TP moved to `price`: an amend of its order. */
export const moveRequest = (line: ChartLine, price: Decimal): LineRequest | null =>
  line.orderId === null || line.field === null
    ? null
    : { _tag: "Amend", orderId: line.orderId, prices: line.field === "limitPrice" ? { limitPrice: price } : { stopPrice: price } }

/** A position's protection with one leg set (a price) or removed (null), the other kept as it is. */
export const protectRequest = (
  position: Position,
  leg: "stopLoss" | "takeProfit",
  price: Decimal | null
): LineRequest => ({
  _tag: "Protect",
  instrumentId: position.instrumentId,
  body: {
    product: position.product,
    stopLoss: leg === "stopLoss" ? price : position.stopLoss?.price ?? null,
    takeProfit: leg === "takeProfit" ? price : position.takeProfit?.price ?? null
  }
})

// --- a working entry's own stop-loss and take-profit (docs/design/0013) ----------------------------

/** The price a working order should fill at (its limit, else its stop); null for a market order. */
export const entryPrice = (order: Order): Decimal | null =>
  order.type._tag === "Limit" || order.type._tag === "StopLimit"
    ? order.type.limitPrice
    : order.type._tag === "Stop"
    ? order.type.stopPrice
    : null

/** The profit (positive) or loss if the order fills at its price and then closes at `price`. */
export const orderPnlAt = (order: Order, price: Decimal): Decimal | null => {
  const entry = entryPrice(order)
  if (entry === null) return null
  const remaining = BigDecimal.subtract(order.qty, order.filledQty)
  const signed = order.side === "Buy" ? remaining : BigDecimal.negate(remaining)
  return BigDecimal.multiply(BigDecimal.subtract(price, entry), signed)
}

/** Where a working order's new stop-loss or take-profit starts: 1% from its price, on its side. */
export const orderStartingPrice = (order: Order, leg: "stopLoss" | "takeProfit", tickSize: Decimal): Decimal | null => {
  const entry = entryPrice(order)
  if (entry === null) return null
  const below = leg === "stopLoss" ? order.side === "Buy" : order.side === "Sell"
  const offset = BigDecimal.multiply(entry, ONE_PERCENT)
  const snapped = roundToStep(below ? BigDecimal.subtract(entry, offset) : BigDecimal.sum(entry, offset), tickSize)
  return BigDecimal.isPositive(snapped) ? snapped : null
}

/** A working order's stop-loss or take-profit set (a price) or removed (null), the other kept. */
export const orderProtectRequest = (order: Order, leg: "stopLoss" | "takeProfit", price: Decimal | null): LineRequest => ({
  _tag: "Amend",
  orderId: order.id,
  prices: {
    protection: {
      stopLoss: leg === "stopLoss" ? price : order.protection?.stopLoss ?? null,
      takeProfit: leg === "takeProfit" ? price : order.protection?.takeProfit ?? null
    }
  }
})

/**
 * A Shift-drag of a working order (TradingView's way): the order to `price`, its stop-loss and
 * take-profit moved by the same amount, in one request.
 */
export const shiftRequest = (order: Order, field: "limitPrice" | "stopPrice", price: Decimal): LineRequest => {
  const entry = entryPrice(order)
  const offset = entry === null ? BigDecimal.fromBigInt(0n) : BigDecimal.subtract(price, entry)
  const moved = (leg: Decimal | undefined) => (leg === undefined ? null : BigDecimal.sum(leg, offset))
  const prices = field === "limitPrice" ? { limitPrice: price } : { stopPrice: price }
  return order.protection === null
    ? { _tag: "Amend", orderId: order.id, prices }
    : {
      _tag: "Amend",
      orderId: order.id,
      prices: { ...prices, protection: { stopLoss: moved(order.protection.stopLoss), takeProfit: moved(order.protection.takeProfit) } }
    }
}

/** The working order with this id on the followed account. */
export const orderById = (state: AccountState | null, orderId: OrderId | null): Order | undefined =>
  state === null || orderId === null ? undefined : state.openOrders.find((order) => order.id === orderId)
